import React from 'react';
import { Bookmark, FileText, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProMetaRow from './ProMetaRow';
import { getPartyBadgeColor } from '../../utils/partyUtils';
import { padDistrictNumber } from '../../utils/districtDisplay';

export interface ProMobileLegislatorCardLegislator {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  partyAffiliation?: string;
  chamber?: string;
  district?: string;
  title?: string;
  email?: string;
  sponsoredBillsCount?: number;
}

export interface ProMobileLegislatorCardProps {
  legislator: ProMobileLegislatorCardLegislator;
  isTracked?: boolean;
  onClick?: (legislator: ProMobileLegislatorCardLegislator) => void;
  className?: string;
}

const getInitials = (leg: ProMobileLegislatorCardLegislator): string => {
  const first = leg.firstName?.[0] || '';
  const last = leg.lastName?.[0] || '';
  if (first || last) return (first + last).toUpperCase();
  const parts = (leg.fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0]?.[0]?.toUpperCase() || '?';
};

const getAvatarTone = (party?: string): string => {
  const p = (party || '').toLowerCase();
  if (p.includes('democrat')) {
    return 'bg-party-democrat-bg dark:bg-party-democrat-bg-dark text-party-democrat-text dark:text-party-democrat-light';
  }
  if (p.includes('republican')) {
    return 'bg-party-republican-bg dark:bg-party-republican-bg-dark text-party-republican-text dark:text-party-republican-light';
  }
  if (p.includes('independ')) {
    return 'bg-party-independent-bg dark:bg-party-independent-bg-dark text-party-independent-text dark:text-party-independent-light';
  }
  return 'bg-party-unknown-bg dark:bg-party-unknown-bg-dark text-party-unknown-text dark:text-party-unknown-text-dark';
};

const getChamberTitle = (leg: ProMobileLegislatorCardLegislator): string => {
  if (leg.title) return leg.title;
  const c = leg.chamber?.toLowerCase();
  if (c?.includes('senate')) return 'Sen.';
  if (c?.includes('house')) return 'Rep.';
  return '';
};

/**
 * Pro-namespaced mobile legislator card — mirrors {@link ProMobileBillCard} layout
 * for consistent list styling across Bills and Legislators tabs.
 */
const ProMobileLegislatorCard: React.FC<ProMobileLegislatorCardProps> = ({
  legislator,
  isTracked = false,
  onClick,
  className = '',
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(legislator);
    } else {
      navigate(`/pro/legislators/${legislator.id}`);
    }
  };

  const fullName =
    legislator.fullName || `${legislator.firstName || ''} ${legislator.lastName || ''}`.trim();
  const initials = getInitials(legislator);
  const avatarTone = getAvatarTone(legislator.partyAffiliation);
  const chamberTitle = getChamberTitle(legislator);
  const districtLabel = [chamberTitle, legislator.district && `District ${padDistrictNumber(legislator.district)}`]
    .filter(Boolean)
    .join(' · ');
  const partyBadge = getPartyBadgeColor(legislator.partyAffiliation);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        w-full text-left overflow-hidden transition-all duration-200
        active:scale-[0.98] touch-manipulation
        rounded-xl bg-white dark:bg-[var(--pro-surface-card)]
        border border-border-light dark:border-white/10 shadow-sm
        ${className}
      `}
    >
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div
            className={`flex-shrink-0 flex items-center justify-center rounded-full w-11 h-11 text-sm font-bold ${avatarTone}`}
          >
            {initials}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base leading-snug text-text-primary dark:text-text-dark-primary truncate">
              {fullName}
            </h3>
            {legislator.partyAffiliation && (
              <span
                className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${partyBadge}`}
              >
                {legislator.partyAffiliation}
              </span>
            )}
          </div>

          {isTracked && (
            <Bookmark
              className="w-4 h-4 flex-shrink-0 text-primary-600 dark:text-primary-400"
              aria-label="Tracked"
            />
          )}
        </div>

        {districtLabel && (
          <div className="mb-3">
            <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wide">
              District
            </span>
            <p className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              {districtLabel}
            </p>
          </div>
        )}

        {typeof legislator.sponsoredBillsCount === 'number' && legislator.sponsoredBillsCount > 0 && (
          <ProMetaRow
            leadingIcon={<FileText className="w-3 h-3" />}
            segments={[`${legislator.sponsoredBillsCount} sponsored bill${legislator.sponsoredBillsCount === 1 ? '' : 's'}`]}
            className="mb-2"
          />
        )}

        {legislator.email && (
          <ProMetaRow
            leadingIcon={<Mail className="w-3 h-3" />}
            segments={[legislator.email]}
          />
        )}
      </div>
    </button>
  );
};

export default ProMobileLegislatorCard;
